import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Upload, Download, AlertCircle, Users, IndianRupee, Search, RefreshCw, ArrowLeft, X, Trash2, CheckCircle2, XCircle, ArrowRightLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';
import Papa from 'papaparse';

const SPORT_ICONS = { table_tennis:'🏓', badminton:'🏸', chess:'♟️', carrom:'🎯', pickleball:'🥒', squash:'🎾', cricket:'🏏', football:'⚽' };
const TEAM_COLORS = ['#ef4444','#3b82f6','#22c55e','#f97316','#8b5cf6','#ec4899','#14b8a6','#f5c518','#6366f1','#a78bfa'];

export default function AuctionPage() {
  const { activeOrgSlug, user, isAdmin } = useAuthStore();
  const admin = isAdmin();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const navigate = useNavigate();

  const [tournaments, setTournaments] = useState([]);
  const [selectedTid, setSelectedTid] = useState('');
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null); // drill-down team name
  const [deletingId, setDeletingId] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [uploadReport, setUploadReport] = useState(null); // shown after upload
  // Transfer player
  const [transferTarget, setTransferTarget]   = useState(null); // pick object to transfer
  const [transferTeamId, setTransferTeamId]   = useState('');
  const [transferBid, setTransferBid]         = useState('');
  const [transferring, setTransferring]       = useState(false);

  useEffect(() => { fetchTournaments(); }, [orgSlug]);
  useEffect(() => { if (selectedTid) fetchPicks(); }, [selectedTid]);

  const token = () => localStorage.getItem('btccp_access_token');

  const fetchTournaments = async () => {
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments`, { headers: { 'Authorization': `Bearer ${token()}` } });
    const data = await res.json();
    const tourns = Array.isArray(data) ? data : [];
    setTournaments(tourns);
    if (tourns.length > 0) setSelectedTid(tourns[0].id);
  };

  const fetchPicks = async () => {
    if (!selectedTid) return;
    setLoading(true);
    const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${selectedTid}/auction/picks`,
      { headers: { 'Authorization': `Bearer ${token()}` } });
    const data = await res.json();
    setPicks(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const handleTemplateDownload = () => {
    // New format matching TeamWiseSportWise.csv
    const csv = 'SponsorName,Name,BidAmt,Category,Badminton,TT,Carrom,Chess,PickleBall\nCircles Kings,Rahul Sharma,15000,Champions,1,1,0,0,1\nCircles Queens,Priya Patel,12000,Advanced,0,1,1,0,0';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'BTCCP_Auction_Template.csv'; a.click();
    URL.revokeObjectURL(url);
    toast.success('Auction template downloaded — matches TeamWiseSportWise format');
  };

  // Sport column name → backend sport code mapping
  const SPORT_COL_MAP = {
    badminton: 'badminton', tt: 'table_tennis', tabletennis: 'table_tennis',
    table_tennis: 'table_tennis', carrom: 'carrom', chess: 'chess',
    pickleball: 'pickleball', pickle_ball: 'pickleball',
  };
  const SPORT_COLS = ['badminton','tt','carrom','chess','pickleball'];

  const handleFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadFile(file);
    Papa.parse(file, { header: true, skipEmptyLines: 'greedy',
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g,'_').replace(/[^\w]/g,''),
      complete: r => setPreview(r.data.slice(0,5)) });
  };

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Select a CSV file'); return; }
    setUploading(true);
    Papa.parse(uploadFile, { header: true, skipEmptyLines: 'greedy',
      transformHeader: h => h.trim().toLowerCase().replace(/\s+/g,'_').replace(/[^\w]/g,''),
      complete: async (results) => {
        try {
          const picks = results.data.map(row => {
            // New format: SponsorName=team, Name=player, BidAmt, Category, Badminton/TT/Carrom/Chess/PickleBall
            const playerName = (row.name || row.player_name || row.player || '').trim();
            const teamName   = (row.sponsorname || row.sponsor_name || row.team_name || row.team || '').trim();
            const bidAmount  = parseFloat(row.bidamt || row.bid_amount || row.bid || row.amount || '0') || 0;
            const rawCategory = (row.category || '').trim().toLowerCase();
            const category = rawCategory === 'champions' ? 'champion' : rawCategory === 'champion' ? 'champion' : 'advanced';

            // Extract sports: columns where value is '1'
            const sports = SPORT_COLS
              .filter(col => row[col] === '1' || row[col] === 1)
              .map(col => SPORT_COL_MAP[col] || col)
              .filter(Boolean);

            return { player_name: playerName, team_name: teamName, bid_amount: bidAmount, category, sports };
          }).filter(p => p.player_name && p.team_name);

          if (!picks.length) {
            const sample = results.meta?.fields?.slice(0,6).join(', ');
            toast.error(`No valid rows. Columns found: [${sample}]. Need SponsorName + Name columns.`);
            setUploading(false);
            return;
          }

          const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${selectedTid}/auction/picks/bulk`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ picks }),
          });
          const data = await res.json();
          if (res.ok) {
            setUploadOpen(false); setUploadFile(null); setPreview([]);
            fetchPicks();
            // Show detailed report instead of dismissible toast
            setUploadReport({ linked: data.linked, errors: data.errors || [], picks: data.picks || [], skipped: data.skipped || [] });
          } else { toast.error(data.detail || 'Upload failed'); }
        } catch (err) { toast.error(`Error: ${err.message}`); }
        finally { setUploading(false); }
      }
    });
  };

  const handleExportPicks = () => {
    const rows = ['Player Name,Team Name,Bid Amount', ...picks.map(p => `${p.player_name},${p.team_name},${p.bid_amount||0}`)];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'auction_results.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeletePick = async (pick) => {
    if (!confirm(`Remove ${pick.player_name} from ${pick.team_name}?`)) return;
    setDeletingId(pick.id);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${selectedTid}/auction/picks/${pick.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token()}` },
      });
      if (res.ok) { toast.success(`${pick.player_name} removed`); fetchPicks(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed to remove'); }
    } catch (_) { toast.error('Failed to remove player'); }
    finally { setDeletingId(null); }
  };

  const handleDeleteTeam = async () => {
    if (!selectedTeam) return;
    const teamPick = picks.find(p => p.team_name === selectedTeam);
    if (!teamPick?.team_id) { toast.error('Team ID not found'); return; }
    if (!confirm(`Delete ALL ${picks.filter(p=>p.team_name===selectedTeam).length} players from ${selectedTeam}? This cannot be undone.`)) return;
    setDeletingTeam(true);
    try {
      const res = await fetch(`/api/orgs/${orgSlug}/tournaments/${selectedTid}/auction/picks/by-team/${teamPick.team_id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token()}` },
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`${d.deleted} players removed from ${selectedTeam}`);
        setSelectedTeam(null);
        fetchPicks();
      } else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch (_) { toast.error('Failed to delete team picks'); }
    finally { setDeletingTeam(false); }
  };

  const handleTransfer = async () => {
    if (!transferTarget || !transferTeamId) { toast.error('Select a target team'); return; }
    setTransferring(true);
    try {
      const res = await fetch(
        `/api/orgs/${orgSlug}/tournaments/${selectedTid}/auction/picks/${transferTarget.id}/transfer`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            new_team_id: transferTeamId,
            ...(transferBid !== '' ? { bid_amount: parseFloat(transferBid) || 0 } : {}),
          }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(`${transferTarget.player_name} transferred to ${data.new_team}`);
        setTransferTarget(null); setTransferTeamId(''); setTransferBid('');
        setSelectedTeam(null);
        fetchPicks();
      } else {
        toast.error(data.detail || 'Transfer failed');
      }
    } catch (_) { toast.error('Transfer failed'); }
    finally { setTransferring(false); }
  };

  const filteredPicks = picks.filter(p => {
    const matchesTeam   = !selectedTeam || p.team_name === selectedTeam;
    const matchesSearch = !search || p.player_name?.toLowerCase().includes(search.toLowerCase()) || p.team_name?.toLowerCase().includes(search.toLowerCase());
    return matchesTeam && matchesSearch;
  });

  // Team summary: group by team, sum bids — include team_id for navigation
  const teamSummary = Object.values(picks.reduce((acc, p) => {
    const k = p.team_name || 'Unknown';
    if (!acc[k]) acc[k] = { team: k, team_id: p.team_id || '', count: 0, total: 0 };
    acc[k].count += 1;
    acc[k].total += parseFloat(p.bid_amount || 0);
    return acc;
  }, {})).sort((a,b) => b.total - a.total);

  const totalSpent = picks.reduce((s, p) => s + parseFloat(p.bid_amount || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-auction-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Auction Results</h1>
          <p className="text-[#8a93a3]">{admin ? 'Upload player auction picks and bid amounts per tournament' : 'View player auction picks and bid amounts'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {admin && (
          <Button onClick={handleTemplateDownload} variant="outline" size="sm"
            className="border-[#252a37] text-[#8a93a3] hover:text-[#f0f2f7]">
            <Download className="h-4 w-4 mr-2"/>Get Template
          </Button>
          )}
          {picks.length > 0 && (
            <Button onClick={handleExportPicks} variant="outline" size="sm"
              className="border-[#252a37] text-[#f0f2f7]">
              <Download className="h-4 w-4 mr-2"/>Export
            </Button>
          )}
          {admin && (
          <Button onClick={() => setUploadOpen(true)} disabled={!selectedTid}
            className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
            data-testid="auction-upload-button">
            <Upload className="h-4 w-4 mr-2"/>Upload Auction CSV
          </Button>
          )}
        </div>
      </div>

      {/* Tournament selector */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <Label className="text-[#8a93a3] flex-shrink-0">Tournament:</Label>
        <select value={selectedTid} onChange={e => setSelectedTid(e.target.value)}
          className="bg-[#161a26] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-1.5 text-sm">
          {tournaments.map(t => <option key={t.id} value={t.id}>{t.name} {t.season ? `(${t.season})` : ''}</option>)}
        </select>
        <Button variant="ghost" size="sm" onClick={fetchPicks} className="text-[#8a93a3]">
          <RefreshCw className="h-4 w-4"/>
        </Button>
      </div>

      {/* Summary cards */}
      {picks.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card onClick={() => navigate('/admin/players')}
            className="bg-[#161a26] border-[#252a37] cursor-pointer hover:border-[rgba(245,197,24,0.4)] hover:-translate-y-0.5 transition-all group">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#f5c518]">{picks.length}</p>
              <p className="text-xs text-[#8a93a3] group-hover:text-[#f0f2f7] transition-colors">Players Auctioned →</p>
            </CardContent>
          </Card>
          <Card onClick={() => navigate('/admin/teams')}
            className="bg-[#161a26] border-[#252a37] cursor-pointer hover:border-[rgba(34,197,94,0.4)] hover:-translate-y-0.5 transition-all group">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#22c55e]">{teamSummary.length}</p>
              <p className="text-xs text-[#8a93a3] group-hover:text-[#f0f2f7] transition-colors">Teams in Auction →</p>
            </CardContent>
          </Card>
          <Card className="bg-[#161a26] border-[#252a37]">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#f0f2f7]">₹{totalSpent.toLocaleString('en-IN')}</p>
              <p className="text-xs text-[#8a93a3]">Total Bids</p>
            </CardContent>
          </Card>
          <Card className="bg-[#161a26] border-[#252a37]">
            <CardContent className="p-4">
              <p className="text-2xl font-bold text-[#3b82f6]">₹{picks.length ? Math.round(totalSpent/picks.length).toLocaleString('en-IN') : 0}</p>
              <p className="text-xs text-[#8a93a3]">Avg Bid</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team purse summary — clickable for drill-down */}
      {teamSummary.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[#8a93a3] uppercase tracking-wider">Team Purse Summary</h3>
            {selectedTeam && (
              <button onClick={() => setSelectedTeam(null)}
                className="text-xs text-[#8a93a3] hover:text-[#f0f2f7] flex items-center gap-1 transition-colors">
                <X className="h-3 w-3"/>Show All Teams
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {teamSummary.map((ts, i) => {
              const color = TEAM_COLORS[i % TEAM_COLORS.length];
              const isSelected = selectedTeam === ts.team;
              return (
                <Card key={ts.team}
                  onClick={() => setSelectedTeam(isSelected ? null : ts.team)}
                  className={`border cursor-pointer transition-all duration-200 select-none ${
                    isSelected
                      ? 'ring-2 ring-[#f5c518] bg-[rgba(245,197,24,0.08)] border-[rgba(245,197,24,0.4)]'
                      : 'bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.3)] hover:bg-[#1a1f2e]'
                  }`}
                  data-testid={`team-purse-card-${ts.team.replace(/\s+/g,'-').toLowerCase()}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        {/* Team color dot */}
                        <div className="h-9 w-9 rounded-lg flex items-center justify-center font-bold text-white text-sm flex-shrink-0"
                          style={{ backgroundColor: color }}>
                          {ts.team?.[0]?.toUpperCase() || 'T'}
                        </div>
                        <div className="min-w-0">
                          <p className={`font-semibold truncate ${isSelected ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>{ts.team}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <p className="text-xs text-[#8a93a3]">{ts.count} player{ts.count !== 1 ? 's' : ''}</p>
                            {ts.team_id && (
                              <button
                                onClick={e => { e.stopPropagation(); navigate(`/admin/teams/${ts.team_id}`); }}
                                className="text-[10px] text-[#3b82f6] hover:text-[#60a5fa] hover:underline transition-colors"
                              >
                                View Team →
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`font-bold text-base ${isSelected ? 'text-[#f5c518]' : 'text-[#f5c518]'}`}>₹{ts.total.toLocaleString('en-IN')}</p>
                        {isSelected && <p className="text-[10px] text-[#f5c518]">▼ viewing</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Picks table header */}
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        {selectedTeam && (
          <div className="flex items-center gap-2 flex-1">
            <button onClick={() => setSelectedTeam(null)}
              className="flex items-center gap-1.5 text-sm text-[#8a93a3] hover:text-[#f0f2f7] transition-colors">
              <ArrowLeft className="h-4 w-4"/>All Teams
            </button>
            <span className="text-[#252a37]">/</span>
            <span className="text-sm font-semibold text-[#f5c518]">{selectedTeam}</span>
            <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] text-xs ml-1">
              {filteredPicks.length} players
            </Badge>
            {admin && (
            <button onClick={handleDeleteTeam} disabled={deletingTeam}
              className="ml-auto flex items-center gap-1.5 text-xs text-[#ef4444] hover:text-[#f87171] border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] hover:bg-[rgba(239,68,68,0.15)] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
              data-testid="delete-team-picks-button">
              <Trash2 className="h-3.5 w-3.5"/>
              {deletingTeam ? 'Deleting...' : `Delete All ${selectedTeam} Picks`}
            </button>
            )}
          </div>
        )}
        {!selectedTeam && (
          <div className="flex-1">
            <p className="text-sm text-[#8a93a3]">
              {filteredPicks.length} player{filteredPicks.length !== 1 ? 's' : ''} — click a team card above to drill down
            </p>
          </div>
        )}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
          <Input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={selectedTeam ? `Search in ${selectedTeam}...` : "Search player or team..."}
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"/>
        </div>
      </div>

      <Card className="bg-[#161a26] border-[#252a37]">
        {loading ? (
          <div className="p-4 space-y-3">{[1,2,3].map(i=><Skeleton key={i} className="h-10 bg-[#1d2230]"/>)}</div>
        ) : filteredPicks.length > 0 ? (
          <ScrollArea className="w-full">
            <Table data-testid="auction-picks-table">
              <TableHeader>
                <TableRow className="border-b border-[#252a37] bg-[#1d2230]/50">
                  <TableHead className="text-xs uppercase text-[#8a93a3]">#</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Player</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3]">Team</TableHead>
                  <TableHead className="text-xs uppercase text-[#8a93a3] text-right">Bid Amount</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPicks.map((pick, idx) => (
                  <TableRow key={pick.id || idx} className="border-b border-[#252a37] hover:bg-[#1d2230]/30">
                    <TableCell className="text-[#8a93a3] text-xs">{idx+1}</TableCell>
                    <TableCell>
                      <div
                        className={`flex items-center gap-2 ${pick.player_id ? 'cursor-pointer group' : ''}`}
                        onClick={() => pick.player_id && navigate(`/admin/players/${pick.player_id}`)}
                      >
                        <div className="h-7 w-7 rounded-full bg-[#1d2230] flex items-center justify-center text-[#f5c518] font-medium text-xs flex-shrink-0">
                          {pick.player_name?.[0]?.toUpperCase() || 'P'}
                        </div>
                        <span className={`font-medium ${pick.player_id ? 'text-[#f0f2f7] group-hover:text-[#f5c518] transition-colors' : 'text-[#f0f2f7]'}`}>
                          {pick.player_name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        onClick={() => pick.team_id && navigate(`/admin/teams/${pick.team_id}`)}
                        className={`text-xs border ${pick.team_id ? 'cursor-pointer hover:bg-[#252a37] hover:text-[#f5c518] hover:border-[rgba(245,197,24,0.4)] transition-colors' : ''} bg-[#1d2230] text-[#8a93a3] border-[#252a37]`}
                      >
                        {pick.team_name}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold text-[#f5c518]">
                      {pick.bid_amount > 0 ? `₹${Number(pick.bid_amount).toLocaleString('en-IN')}` : '—'}
                    </TableCell>
                    <TableCell className="text-right pr-3">
                      {admin && (
                      <button
                        onClick={() => { setTransferTarget(pick); setTransferTeamId(''); setTransferBid(pick.bid_amount || ''); }}
                        className="p-1.5 rounded-lg text-[#8a93a3] hover:text-[#f5c518] hover:bg-[rgba(245,197,24,0.1)] transition-colors"
                        title={`Transfer ${pick.player_name} to another team`}
                        data-testid={`transfer-player-${pick.id}`}
                      >
                        <ArrowRightLeft className="h-3.5 w-3.5"/>
                      </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <ScrollBar orientation="horizontal"/>
          </ScrollArea>
        ) : (
          <CardContent className="p-12 text-center">
            <IndianRupee className="h-12 w-12 text-[#8a93a3] mx-auto mb-4"/>
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
              {selectedTid ? 'No auction results yet' : 'Select a tournament'}
            </h3>
            <p className="text-[#8a93a3] mb-4">
              {admin ? 'Upload the auction CSV to see player picks and bid amounts' : 'No auction results available for this tournament yet'}
            </p>
            {selectedTid && admin && (
              <Button onClick={() => setUploadOpen(true)} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                <Upload className="h-4 w-4 mr-2"/>Upload Auction Results
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={o => { setUploadOpen(o); if (!o) { setUploadFile(null); setPreview([]); } }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="auction-upload-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#f5c518]"/>Upload Auction Results
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#1d2230] rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-[#f5c518] flex-shrink-0 mt-0.5"/>
              <div className="text-xs text-[#8a93a3]">
                <p><strong className="text-[#f0f2f7]">CSV format (TeamWiseSportWise):</strong></p>
                <p><code className="text-[#f5c518]">SponsorName</code> (team), <code className="text-[#f5c518]">Name</code> (player), <code className="text-[#f5c518]">BidAmt</code>, Category, Badminton, TT, Carrom, Chess, PickleBall</p>
                <p className="mt-1">Sports marked <code className="text-[#f5c518]">1</code> = player plays that sport. Category: <code className="text-[#f5c518]">Champions</code> / <code className="text-[#f5c518]">Advanced</code>. Players matched by name to existing members.</p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-[#8a93a3]">Choose CSV File</Label>
              {admin && (
              <Button size="sm" variant="ghost" onClick={handleTemplateDownload}
                className="text-xs text-[#f5c518] hover:text-[#f7d24a] h-7 px-2">
                <Download className="h-3 w-3 mr-1"/>Get Template
              </Button>
              )}
            </div>
            <input type="file" accept=".csv" onChange={handleFileChange}
              className="block w-full text-sm text-[#8a93a3] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#1d2230] file:text-[#f5c518] file:font-medium hover:file:bg-[#252a37] cursor-pointer"
              data-testid="auction-file-input"/>
            {preview.length > 0 && (
              <div className="bg-[#1d2230] rounded-lg overflow-x-auto">
                <p className="text-xs text-[#8a93a3] px-3 pt-2">Preview ({preview.length} rows)</p>
                <table className="w-full text-xs mt-1">
                  <thead><tr className="border-b border-[#252a37]">
                    {Object.keys(preview[0]).map(h=><th key={h} className="px-3 py-1.5 text-left text-[#8a93a3]">{h}</th>)}
                  </tr></thead>
                  <tbody>{preview.map((row,i)=>(
                    <tr key={`preview-${i}-${Object.values(row)[0]}`} className="border-b border-[#252a37]/50">
                      {Object.values(row).map((v,j)=><td key={`cell-${j}`} className="px-3 py-1.5 text-[#f0f2f7] truncate max-w-[120px]">{v}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleUpload} disabled={!uploadFile || uploading}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="auction-upload-submit">
              {uploading ? 'Linking Players...' : 'Upload & Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Report Dialog */}
      <Dialog open={!!uploadReport} onOpenChange={() => setUploadReport(null)}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-lg" data-testid="upload-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <Trophy className="h-5 w-5 text-[#f5c518]"/>Auction Upload Report
            </DialogTitle>
          </DialogHeader>
          {uploadReport && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.3)] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#22c55e]">{uploadReport.linked}</p>
                  <p className="text-xs text-[#8a93a3] mt-0.5">Newly added</p>
                </div>
                <div className="bg-[#1d2230] border border-[#252a37] rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-[#8a93a3]">{uploadReport.skipped?.length || 0}</p>
                  <p className="text-xs text-[#8a93a3] mt-0.5">Already exist (skipped)</p>
                </div>
                <div className={`border rounded-lg p-3 text-center ${uploadReport.errors.length > 0 ? 'bg-[rgba(239,68,68,0.08)] border-[rgba(239,68,68,0.3)]' : 'bg-[#1d2230] border-[#252a37]'}`}>
                  <p className={`text-2xl font-bold ${uploadReport.errors.length > 0 ? 'text-[#ef4444]' : 'text-[#22c55e]'}`}>{uploadReport.errors.length}</p>
                  <p className="text-xs text-[#8a93a3] mt-0.5">Not matched</p>
                </div>
              </div>

              {/* Not found list */}
              {uploadReport.errors.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-[#ef4444] flex items-center gap-1.5 mb-2">
                    <XCircle className="h-4 w-4"/>Players not found in member database:
                  </p>
                  <div className="bg-[#1d2230] rounded-lg border border-[#252a37] max-h-48 overflow-y-auto">
                    {uploadReport.errors.map((err, i) => (
                      <div key={`err-${i}`} className="px-3 py-2 border-b border-[#252a37]/50 last:border-0 text-xs">
                        <span className="text-[#ef4444]">{err.replace(' (check spelling)', '')}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#8a93a3] mt-2">
                    These names did not match any member. Check spelling in your CSV vs the Members list, then re-upload.
                  </p>
                </div>
              )}

              {/* Linked list */}
              {uploadReport.picks.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-[#22c55e] flex items-center gap-1.5 mb-2">
                    <CheckCircle2 className="h-4 w-4"/>Successfully linked:
                  </p>
                  <div className="bg-[#1d2230] rounded-lg border border-[#252a37] max-h-40 overflow-y-auto">
                    {uploadReport.picks.map((p, i) => (
                      <div key={`pick-${p.player}-${i}`} className="flex items-center justify-between px-3 py-1.5 border-b border-[#252a37]/50 last:border-0 text-xs">
                        <span className="text-[#f0f2f7]">{p.player}</span>
                        <span className="text-[#8a93a3]">{p.team} · ₹{(p.bid||0).toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setUploadReport(null)} className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Player Dialog */}
      <Dialog open={!!transferTarget} onOpenChange={o => { if (!o) { setTransferTarget(null); setTransferTeamId(''); setTransferBid(''); } }}>
        <DialogContent className="bg-[#161a26] border-[#252a37] max-w-sm" data-testid="transfer-player-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#f0f2f7] flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-[#f5c518]"/>Transfer Player
            </DialogTitle>
          </DialogHeader>
          {transferTarget && (
            <div className="space-y-4">
              <div className="bg-[#1d2230] rounded-lg p-3">
                <p className="font-semibold text-[#f0f2f7]">{transferTarget.player_name}</p>
                <p className="text-xs text-[#8a93a3] mt-0.5">
                  Current team: <span className="text-[#ef4444]">{transferTarget.team_name}</span>
                  {transferTarget.bid_amount > 0 && ` · ₹${Number(transferTarget.bid_amount).toLocaleString('en-IN')}`}
                </p>
              </div>
              <div>
                <Label className="text-[#8a93a3] mb-1 block">Transfer to Team *</Label>
                <select value={transferTeamId} onChange={e => setTransferTeamId(e.target.value)}
                  className="w-full bg-[#1d2230] border border-[#252a37] text-[#f0f2f7] rounded-md px-3 py-2 text-sm"
                  data-testid="transfer-team-select">
                  <option value="">Select new team</option>
                  {teamSummary
                    .filter(ts => ts.team !== transferTarget.team_name)
                    .map(ts => (
                      <option key={ts.team_id || ts.team} value={ts.team_id}>{ts.team}</option>
                    ))
                  }
                </select>
              </div>
              <div>
                <Label className="text-[#8a93a3] mb-1 block">Update Bid Amount (optional)</Label>
                <Input type="number" min="0" value={transferBid}
                  onChange={e => setTransferBid(e.target.value)}
                  placeholder="Keep same bid amount"
                  className="bg-[#1d2230] border-[#252a37] text-[#f0f2f7]"
                  data-testid="transfer-bid-input"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTarget(null)} className="border-[#252a37] text-[#f0f2f7]">Cancel</Button>
            <Button onClick={handleTransfer} disabled={transferring || !transferTeamId}
              className="bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]"
              data-testid="transfer-confirm-button">
              {transferring ? 'Transferring...' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
